import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { effect, Injectable, signal, WritableSignal } from '@angular/core';
import { McpService } from '../mcp.service';
import { Node } from '../../components/explorer/node';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { RepoService } from '../repo.service';
import { DbService } from '../db.service';
import { Mutex } from '../mutex.service';
import { components } from '../../schema/backend';
import { OauthService } from '../oauth.service';

type Status = 'not_started' | 'in_progress' | 'completed' | 'failed';
export type StateData = {
  aiThought: string;
  extractedData: object;
}
type StateInfo = {
  status: Status;
  data: StateData | null;
  dependsOn: string[] | null;
};
type StatesMap = {
  [stateName: string]: StateInfo;
};

// Parameters for calling a tool
type CallToolRequestParams = {
  tool_name: string;
  arguments?: { [key: string]: unknown } | null;
}

type ErrorResponse = {
  type: 'ErrorResponse';
  message: string;
}

type TextResponse = {
  type: 'TextResponse';
  text: string;
}

type ToolCallRequest = {
  type: 'ToolCallRequest';
  tool_call: CallToolRequestParams;
  tool_call_id: string;
}

type FinishResponse<T> = {
  type: 'FinishResponse';
  result: T;
}

type OsbUploadResponse = {
  type: 'OsbUploadResponse';
  success: boolean;
  message: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
};

export type OsbStudy = {
  uid: string;
  id: string | null;
  acronym: string;
};

type UsdmStateInput = components['schemas']['UsdmStateExtractInput'];

export type UsdmSession = {
  id: string;
  title: string;
  private_osb_study_id: string | null;
  public_osb_study_id: string | null;
  user_id: string;
  session_id: string;
  repo_id: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class Usdm2Service {

  sessionId!: string;
  currentState = signal<string | null>(null);
  private currentStateUpdatesStream = new BehaviorSubject<string | null>(null);
  currentStateUpdatesStream$ = this.currentStateUpdatesStream.asObservable();

  private contentUpdate = new BehaviorSubject<OsbUploadResponse | FinishResponse<unknown> | null>(null);
  contentUpdate$ = this.contentUpdate.asObservable();

  private usdmSessionIdSubject = new BehaviorSubject<string | null>(null);
  usdmSessionId$ = this.usdmSessionIdSubject.asObservable();

  setUsdmSessionId(sessionId: string): void {
    this.usdmSessionIdSubject.next(sessionId);
    this.sessionId = sessionId;
  }

  setContentUpdate(item: OsbUploadResponse | FinishResponse<unknown> | null): void {
    this.contentUpdate.next(item);
  }

  usdmSession: WritableSignal<UsdmSession | null> = signal<UsdmSession | null>(null);

  stateCompletionResolver: (() => void) | null = null;

  states: StatesMap = {};
  isRunningState = signal<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tools: any = null;
  protocol: Node | null = null;

  osbServerUrl: string | null = null;
  publicOsbStudyId: string | null = null;
  privateOsbStudyId: string | null = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  criteria: any = null;

  soaTables: string | null = null;

  aiWriting = signal<boolean>(false);

  constructor(
    private http: HttpClient,
    private mcp: McpService,
    private repoService: RepoService,
    private mutex: Mutex,
    private oauth: OauthService,
    private db: DbService,
  ) {
    // this.sessionId = crypto.randomUUID();

    this.states = {
      'Debug': { status: 'not_started', data: null, dependsOn: null },
      'Debug2': { status: 'not_started', data: null, dependsOn: null },
      // 'Debug3': { status: 'not_started', data: null },
      'CreateStudy': { status: 'not_started', data: null, dependsOn: null },
      'Properties': { status: 'not_started', data: null, dependsOn: null },
      'Arms': { status: 'not_started', data: null, dependsOn: null },
      'Population': { status: 'not_started', data: null, dependsOn: null },
      'Objectives': { status: 'not_started', data: null, dependsOn: null },
      'Endpoints': { status: 'not_started', data: null, dependsOn: ['Objectives'] },
      'Elements': { status: 'not_started', data: null, dependsOn: null },
      'Find SOA Tables': { status: 'not_started', data: null, dependsOn: null },
      'Epochs & Visits': { status: 'not_started', data: null, dependsOn: ['Find SOA Tables'] },
      'Activities': { status: 'not_started', data: null, dependsOn: ['Find SOA Tables'] },
      'Schedule Of Activities': { status: 'not_started', data: null, dependsOn: ['Find SOA Tables', 'Epochs & Visits', 'Activities'] },
      'Study Design Cells': { status: 'not_started', data: null, dependsOn: ['Arms', 'Elements', 'Epochs & Visits'] },
      'Criteria': { status: 'not_started', data: null, dependsOn: null },
      'Download USDM': { status: 'not_started', data: null, dependsOn: null },
    };
    effect(() => {
      if (this.repoService.repo() != null) {
        this.mcp.connect().then(() => {
          this.mcp.client.listTools().then(tools => {
            this.tools = tools.tools.filter((tool: { name: string }) => tool.name.startsWith('pdf_'));
          });
        });
      }
    });
  }

  public signalStateCompletion(stateName: string): void {
    if (this.currentState() !== stateName) {
      console.warn(`Ignored signal for '${stateName}'. Currently processing '${this.currentState()}'.`);
      return;
    }

    if (this.stateCompletionResolver) {
      console.log("Signaling state completion for:", stateName);
      this.stateCompletionResolver();
      this.stateCompletionResolver = null;
    } else {
      console.warn(`Signal received for '${stateName}', but the operation is already complete or not waiting.`);
    }
  }

  async dummyRunState(stateName: string) {
    if (this.states[stateName]?.dependsOn && this.states[stateName]?.dependsOn.some(dep => this.states[dep]?.status !== 'completed')) {
      throw new Error(`State ${stateName} depends on ${this.states[stateName]?.dependsOn.join(', ')} but they are not completed`, { cause: { code: 'STATE_DEPENDENCY_NOT_COMPLETED', dependencies: this.states[stateName]?.dependsOn, stateName: stateName } });
    }
    this.aiWriting.set(true);
    this.isRunningState.set(true);
    this.currentState.set(stateName);
    this.updateStateStatus(stateName, 'in_progress');

    let resolver: () => void;
    const completionPromise = new Promise<void>((r) => { resolver = r; });
    this.stateCompletionResolver = resolver!;

    await new Promise(resolve => setTimeout(resolve, 1000));
    this.contentUpdate.next({ type: 'OsbUploadResponse', success: true, message: 'Dummy upload ongoing', data: {} });
    await new Promise(resolve => setTimeout(resolve, 2000));
    this.contentUpdate.next({ type: 'OsbUploadResponse', success: true, message: 'Dummy upload still going', data: {} });
    await new Promise(resolve => setTimeout(resolve, 1000));
    this.contentUpdate.next({ type: 'FinishResponse', result: {} });

    console.log(`Waiting for state ${stateName} completion...`);
    await completionPromise;

    this.updateStateStatus(stateName, 'completed');
    this.currentState.set(null);
    this.isRunningState.set(false);
  }

  async runState(stateName: string, userMsg: string | null): Promise<void> {
    console.log(`Session: ${this.sessionId}`)
    if (this.states[stateName]?.dependsOn && this.states[stateName]?.dependsOn.some(dep => this.states[dep]?.status !== 'completed')) {
      throw new Error(`State ${stateName} depends on ${this.states[stateName]?.dependsOn.join(', ')} but they are not completed`, { cause: { code: 'STATE_DEPENDENCY_NOT_COMPLETED', dependencies: this.states[stateName]?.dependsOn, stateName: stateName } });
    }

    let resolver: () => void;
    const completionPromise = new Promise<void>((r) => { resolver = r; });
    this.stateCompletionResolver = resolver!;

    this.aiWriting.set(true);
    this.isRunningState.set(true);
    this.currentState.set(stateName);
    this.updateStateStatus(stateName, 'in_progress');
    console.log(`Running state: ${this.currentState()}`);

    const controller = new AbortController();
    let buffer = '';
    let release: (() => void) | null = null;
    try {
      const stream = await this.extractApi(userMsg, controller.signal);
      let finished = false;
      let extractedData: object | null = null;
      release = await this.mutex.acquire();
      for await (const item of stream) {
        // console.log("Token: ", item);
        release();
        release = null;
        try {
          if (!this.aiWriting()) {
            controller.abort();
            if (this.stateCompletionResolver){  
              this.stateCompletionResolver();
              this.stateCompletionResolver = null;
            }
            break;
          }

          if (item.type === 'ErrorResponse') {
            throw new Error(item.message);
          } else if (item.type === 'TextResponse') {
            buffer += item.text;
            this.currentStateUpdatesStream.next(buffer);
          } else if (item.type === 'ToolCallRequest') {
            // console.log('Received ToolCallRequest:', item);
            const toolResult = await this.mcp.callMcpToolRaw(item.tool_call.tool_name, item.tool_call.arguments || {})

            // console.log('Tool result:', toolResult);
            await lastValueFrom(this.http.post(`${environment.backendUrl}/workflows/usdm/tool_result/${item.tool_call_id}`, {
              result: toolResult
            }));
            // console.log('Sent tool result for tool_call_id:', item.tool_call_id);
          } else if (item.type === 'FinishResponse') {
            // console.log('Received FinishResponse:', item);
            finished = true;
            extractedData = item.result || null;
            this.contentUpdate.next(item);
            break;
          } else if (item.type === 'OsbUploadResponse') {
            // console.log('Received OsbUploadResponse:', item);
            if (item.success) {
              buffer += "\n\n>   " + `<span class="text-green-600 font-semibold">${item.message}</span>`;
            } else {
              buffer += "\n\n>   " + `<span class="text-red-400 font-semibold">${item.message}</span>`;
            }
            // buffer += "\n\n>   " + item.message;
            this.currentStateUpdatesStream.next(buffer);
            this.contentUpdate.next(item);
          }
        } finally {
          release = await this.mutex.acquire();
        }
      }
      release?.();
      release = null;
      if (!finished) {
        throw new Error(`${stateName} extraction ended without a result. Please retry this step.`);
      }
      console.log(`Waiting for state ${stateName} completion...`);
      await completionPromise;

      this.updateStateStatus(stateName, 'completed', {
        aiThought: buffer,
        extractedData: extractedData || {}
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Extraction failed. Please retry this step.';
      buffer += `\n\nExtraction failed: ${message}`;
      this.updateStateStatus(stateName, 'failed', { aiThought: buffer, extractedData: {} });
      this.currentStateUpdatesStream.next(buffer);
    } finally {
      release?.();
      controller.abort();
      this.stateCompletionResolver = null;
      if (this.states[stateName]?.status !== 'failed') this.currentStateUpdatesStream.next(null);
      this.isRunningState.set(false);
      this.aiWriting.set(false);
    }
  }

  getDataForState(stateName: string): StateData | null {
    console.log(`Getting data for state: ${stateName}`);
    return null;
  }

  updateStateStatus(stateName: string, status: Status, data: StateData | null = null): void {
    if (this.states[stateName]) {
      this.states[stateName].status = status;
      this.states[stateName].data = data;
      console.log(`Updated state: ${stateName} to status: ${status}`);
    } else {
      throw new Error(`State with name ${stateName} not found`, { cause: { code: 'UPDATE_FAILED_STATE_NOT_FOUND' } });
    }
  }

  getStatusForState(stateName: string): StateInfo | null {
    return this.states[stateName] || null;
  }

  getStateNames(): string[] {
    return Object.keys(this.states);
  }

  async extractApi<T>(userMsg: string | null, signal?: AbortSignal): Promise<AsyncIterable<TextResponse | ToolCallRequest | FinishResponse<T> | OsbUploadResponse | ErrorResponse>> {
    const token = sessionStorage.getItem('userAccessToken')
    const osbUrl = this.osbServerUrl?.replace(/\/$/, '') + "/api"
    const data: UsdmStateInput = {
      session_id: this.sessionId,
      repo_id: this.repoService.repo() ? this.repoService.repo()!.id : null,
      study_id: this.privateOsbStudyId,
      public_study_id: this.publicOsbStudyId,
      user_msg: userMsg,
      state: this.currentState()!,
      protocol_path: this.protocol?.path!,
      osb_server_url: osbUrl,
      tools: this.tools,
      soa_tables: this.soaTables,
    }
    console.log('Extracting API data:', data);
    const stream = {
      async *[Symbol.asyncIterator]() {
        const url = `${environment.backendUrl}/workflows/usdm/extract`;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token || ''}`,
          },
          body: JSON.stringify(data),
          signal
        });

        if (!response.ok) {
          throw new Error(`Extraction request failed (${response.status})`);
        }
        if (!response.body) {
          throw new Error('Response body is null');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = ''; // Buffer to accumulate incomplete chunks

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const text = decoder.decode(value, { stream: !done });
            buffer += text;

            const lines = buffer.split('\n');

            // Keep the last line in the buffer (it might be incomplete)
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.trim()) {
                try {
                  const json = JSON.parse(line);
                  yield json;
                } catch (e) {
                  // If a complete line (separated by \n) fails to parse, it's likely corrupted data
                  console.error('Failed to parse complete JSON line:', line.substring(0, 100), e);
                }
              }
            }
          }

          // Process any remaining data in the buffer
          if (buffer.trim()) {
            try {
              const json = JSON.parse(buffer);
              yield json;
            } catch (e) {
              console.error('Failed to parse final buffer:', buffer.substring(0, 100), e);
            }
          }
        } finally {
          reader.releaseLock();
        }
      },
    };

    return stream;
  }

  async getAllUsdmSessions(): Promise<UsdmSession[]> {
    if (!this.oauth.user) {
      throw new Error('User info is not available', {
        cause: { code: 'USER_INFO_NOT_AVAILABLE' },
      });
    }
    let data;
    try {
      data = await this.db.listUsdmSessions();
    } catch (error) {
      throw new Error('Failed to fetch chats', {
        cause: { code: 'FETCH_CHATS_ERROR', error },
      });
    }
    const sessions: UsdmSession[] = data.map((session) => ({
      id: session.id,
      public_osb_study_id: session.osb_study_id,
      private_osb_study_id: null,
      title: '',
      user_id: session.user_id,
      session_id: session.session_id,
      repo_id: session.repo_id,
    }));

    try {
      const response = await lastValueFrom(this.http.get<OsbStudy[]>(`${environment.osburl}/api/studies/list?minimal=true`, {}));
      for (const session of sessions) {
        const item = response.find((item) => item.id === session.public_osb_study_id);
        if (item) {
          session.private_osb_study_id = item.uid;
          session.title = item.acronym;
        }
      }
    } catch (error) {
      throw new Error('Failed to fetch OSB studies', {
        cause: { code: 'FETCH_OSB_STUDIES_ERROR', error },
      });
    }
    // console.log('OSB studies:', response);
    // for (const item of response) {
    //   const session = sessions.find((session) => session.public_osb_study_id === item.id);
    //   if (session) {
    //     session.private_osb_study_id = item.uid;
    //     session.title = item.acronym;
    //   }
    // }
    // console.log('Sessions with private OSB study IDs:', sessions);
    return sessions;
  }
}
